import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Sparkles, 
  Check, 
  X, 
  RefreshCw,
  Link2,
  Unlink,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  FileInput,
  Receipt,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Eye,
  Landmark
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const RiconciliazioneBancaria = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [unreconciled, setUnreconciled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, unreconciledRes] = await Promise.all([
        axios.get(`${API}/reconciliation/stats`, getAuthHeaders()),
        axios.get(`${API}/reconciliation/unreconciled`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setUnreconciled(unreconciledRes.data.unreconciled || []);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const runAIAnalysis = async () => {
    setAnalyzing(true);
    setSuggestions([]);
    try {
      const response = await axios.post(
        `${API}/reconciliation/analyze`, 
        {}, 
        {
          ...getAuthHeaders(),
          timeout: 60000  // 60 seconds timeout for AI analysis
        }
      );
      setSuggestions(response.data.suggestions || []);
      if (response.data.suggestions?.length > 0) {
        toast.success(`Trovate ${response.data.suggestions.length} corrispondenze`);
      } else {
        toast.info("Nessuna corrispondenza trovata");
      }
    } catch (error) {
      console.error("AI Analysis error:", error);
      toast.error("Errore nell'analisi AI: " + (error.message || "timeout"));
    } finally {
      setAnalyzing(false);
    }
  };

  const confirmMatch = async (suggestion) => {
    setConfirmingId(suggestion.bank_entry_id);
    try {
      await axios.post(`${API}/reconciliation/confirm`, {
        bank_entry_id: suggestion.bank_entry_id,
        matched_type: suggestion.matched_type,
        matched_id: suggestion.matched_id,
        confidence: suggestion.confidence,
        status: "confirmed"
      }, getAuthHeaders());
      
      toast.success("Riconciliazione confermata");
      
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.bank_entry_id !== suggestion.bank_entry_id));
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error("Errore nella conferma");
    } finally {
      setConfirmingId(null);
    }
  };

  const rejectMatch = async (bankEntryId) => {
    try {
      await axios.post(`${API}/reconciliation/reject/${bankEntryId}`, {}, getAuthHeaders());
      toast.success("Suggerimento rifiutato");
      setSuggestions(prev => prev.filter(s => s.bank_entry_id !== bankEntryId));
    } catch (error) {
      toast.error("Errore nel rifiuto");
    }
  };

  const getMatchTypeIcon = (type) => {
    switch (type) {
      case "fattura_attiva": return <FileText className="w-4 h-4 text-[#34C759]" />;
      case "fattura_passiva": return <FileInput className="w-4 h-4 text-[#FF3B30]" />;
      case "cost": return <Receipt className="w-4 h-4 text-[#FF9500]" />;
      default: return <Link2 className="w-4 h-4" />;
    }
  };

  const getMatchTypeName = (type) => {
    switch (type) {
      case "fattura_attiva": return "Fattura Attiva";
      case "fattura_passiva": return "Fattura Passiva";
      case "cost": return "Costo";
      default: return type;
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 90) return <Badge className="bg-[#34C759] text-white">{confidence}%</Badge>;
    if (confidence >= 70) return <Badge className="bg-[#007AFF] text-white">{confidence}%</Badge>;
    if (confidence >= 50) return <Badge className="bg-[#FF9500] text-white">{confidence}%</Badge>;
    return <Badge className="bg-[#86868B] text-white">{confidence}%</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="riconciliazione-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#AF52DE]" />
            Riconciliazione Bancaria AI
          </h1>
          <p className="page-subtitle">Abbinamento automatico movimenti bancari con fatture e costi</p>
        </div>
        <Button
          onClick={runAIAnalysis}
          disabled={analyzing || !unreconciled.length}
          className="bg-gradient-to-r from-[#AF52DE] to-[#007AFF] hover:opacity-90 text-white rounded-full px-6"
          data-testid="analyze-btn"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analisi in corso...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analizza con AI
            </>
          )}
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Movimenti Totali</p>
                  <p className="text-xl font-semibold text-[#1D1D1F]">{stats.total_entries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#34C759]/10 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Riconciliati</p>
                  <p className="text-xl font-semibold text-[#34C759]">{stats.reconciled_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF9500]/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#FF9500]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">In Attesa</p>
                  <p className="text-xl font-semibold text-[#FF9500]">{stats.pending_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#AF52DE]/10 rounded-xl flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-[#AF52DE]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868B] uppercase">Tasso Riconciliazione</p>
                  <p className="text-xl font-semibold text-[#AF52DE]">{stats.reconciliation_rate}%</p>
                </div>
              </div>
              <Progress value={stats.reconciliation_rate} className="mt-2 h-2" />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Amounts Summary */}
      {stats?.totals && (
        <Card className="bg-gradient-to-r from-[#007AFF]/5 to-[#34C759]/5 rounded-2xl border border-[#007AFF]/20 mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase mb-1">Entrate Totali</p>
                <p className="text-lg font-semibold text-[#34C759]">{formatCurrency(stats.totals.total_income)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase mb-1">Entrate Riconciliate</p>
                <p className="text-lg font-semibold text-[#34C759]">{formatCurrency(stats.totals.reconciled_income)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase mb-1">Uscite Totali</p>
                <p className="text-lg font-semibold text-[#FF3B30]">{formatCurrency(stats.totals.total_expense)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase mb-1">Uscite Riconciliate</p>
                <p className="text-lg font-semibold text-[#FF3B30]">{formatCurrency(stats.totals.reconciled_expense)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#AF52DE]" />
              Suggerimenti AI ({suggestions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <div 
                  key={suggestion.bank_entry_id}
                  className="p-4 bg-[#F5F5F7] rounded-xl border border-[#E5E5E5]"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Bank Entry */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          suggestion.bank_entry.amount >= 0 ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                        }`}>
                          {suggestion.bank_entry.amount >= 0 ? (
                            <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#1D1D1F]">Movimento Bancario</p>
                          <p className="text-sm text-[#86868B]">
                            {new Date(suggestion.bank_entry.date).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-[#1D1D1F] mb-1 truncate">
                        {suggestion.bank_entry.description}
                      </p>
                      <p className={`font-semibold ${suggestion.bank_entry.amount >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                        {formatCurrency(suggestion.bank_entry.amount)}
                      </p>
                    </div>

                    {/* Match Arrow */}
                    <div className="flex flex-col items-center justify-center px-4">
                      <Link2 className="w-6 h-6 text-[#AF52DE]" />
                      {getConfidenceBadge(suggestion.confidence)}
                    </div>

                    {/* Matched Document */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#007AFF]/10">
                          {getMatchTypeIcon(suggestion.matched_type)}
                        </div>
                        <div>
                          <p className="font-medium text-[#1D1D1F]">{getMatchTypeName(suggestion.matched_type)}</p>
                          <p className="text-sm text-[#86868B]">
                            {suggestion.matched_document.numero || suggestion.matched_document.description?.substring(0, 30)}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-[#1D1D1F] mb-1">
                        {suggestion.matched_document.cliente || 
                         suggestion.matched_document.fornitore || 
                         suggestion.matched_document.category}
                      </p>
                      <p className="font-semibold text-[#1D1D1F]">
                        {formatCurrency(suggestion.matched_document.totale || suggestion.matched_document.amount)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => confirmMatch(suggestion)}
                        disabled={confirmingId === suggestion.bank_entry_id}
                        className="bg-[#34C759] hover:bg-[#2DB550] text-white"
                        size="sm"
                      >
                        {confirmingId === suggestion.bank_entry_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Conferma
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => rejectMatch(suggestion.bank_entry_id)}
                        variant="outline"
                        className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        size="sm"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                  
                  {suggestion.reason && (
                    <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                      <p className="text-sm text-[#86868B]">
                        <Sparkles className="w-3 h-3 inline mr-1 text-[#AF52DE]" />
                        {suggestion.reason}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unreconciled Entries */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Movimenti Non Riconciliati ({unreconciled.length})
          </CardTitle>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="text-[#007AFF]"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Aggiorna
          </Button>
        </CardHeader>
        <CardContent>
          {unreconciled.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unreconciled.slice(0, 20).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-[#86868B]">
                      {new Date(entry.date).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell>
                      <p className="text-[#1D1D1F] truncate max-w-[300px]">
                        {entry.description}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.category || 'Altro'}</Badge>
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${
                      entry.amount >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                    }`}>
                      {formatCurrency(entry.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <CheckCircle2 className="w-16 h-16 text-[#34C759] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">
                Tutti i movimenti sono riconciliati!
              </h3>
              <p className="text-[#86868B]">
                Non ci sono movimenti bancari in attesa di riconciliazione
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Empty State for Bank Entries */}
      {stats?.total_entries === 0 && (
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mt-6">
          <CardContent className="py-12 text-center">
            <Landmark className="w-16 h-16 text-[#86868B] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">
              Nessun estratto conto importato
            </h3>
            <p className="text-[#86868B] mb-4">
              Importa un estratto conto bancario per iniziare la riconciliazione
            </p>
            <Button
              onClick={() => window.location.href = '/import-export'}
              className="bg-[#007AFF] hover:bg-[#0066CC]"
            >
              Vai a Import/Export
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RiconciliazioneBancaria;
